import ApiStateListeners from './ApiStateListeners';
import Common from './Common';
import { USE_SAML, setApiUrl } from './Config';
import SAML from './SAML';
import StateEvent from './StateEvent';
import Storage from './Storage';
import Acl from './calls/Acl';
import Calendar from './calls/Calendar';
import Callback from './calls/Callback';
import Captcha from './calls/Captcha';
import Cfc from './calls/Cfc';
import Color from './calls/Color';
import Communicator from './calls/Communicator';
import Companies from './calls/Companies';
import Company from './calls/Company';
import Contact from './calls/Contact';
import ContactImport from './calls/ContactImport';
import Country from './calls/Country';
import DataDeletion from './calls/DataDeletion';
import Icon from './calls/Icon';
import Login from './calls/Login';
import Mailbox from './calls/Mailbox';
import PbxDad from './calls/PbxDad';
import Profile from './calls/Profile';
import Queue from './calls/Queue';
import Recording from './calls/Recording';
import ResetPassword from './calls/ResetPassword';
import SMSLogin from './calls/SMSLogin';
import Schedule from './calls/Schedule';
import Sound from './calls/Sound';
import Teams from './calls/Teams';
import Timezone from './calls/Timezone';
import Unread from './calls/Unread';
import User from './calls/User';
import Verify from './calls/Verify';
import StorageTypes from './storage/Types';

const userThingsToDelete = [
	"fields",
	"vm_deletevoicemail",
	"vm_email",
	"vm_pager",
	"vm_password",
	"microsoft_exchange_user",
	"microsoft_skype_user",
	"note_text",
	"note_title",
	"tele2_bill_e164",
	"schedule_pbx_profile",
];

export interface Options {
	useSAML?: boolean,
	useRefreshToken?: boolean,
	storageType?: StorageTypes,
	accessToken?: string,
	apiUrl?: string
}

// TODO Allow caching of calls for example retrieving users and invalidate it if we get the "UserUpdate" push event
// TODO private functions
// TODO save things in localStorage or sessionStorage instead of cookie?
// TODO use refresh_token if access_token is timed out
export default class {
	headers: ApiHeaders;
	accessToken?: string;
	oauthData: { access_token?: string };
	user?: any;
	username?: string;
	password?: string;
	clientId: string;
	useSAML: boolean;
	useRefreshToken: boolean;
	refreshTokenTimer?: ReturnType<typeof setTimeout>;
	storedData: {[key: string]: any};
	callback?: (event: string, b: boolean) => void;
	storage: InstanceType<typeof Storage>;
	saml: InstanceType<typeof SAML>;
	calls: {
		login: InstanceType<typeof Login>,
		smsLogin: InstanceType<typeof SMSLogin>,
		user: InstanceType<typeof User>,
		queue: InstanceType<typeof Queue>,
		company: InstanceType<typeof Company>,
		captcha: InstanceType<typeof Captcha>,
		resetPassword: InstanceType<typeof ResetPassword>,
		contact: InstanceType<typeof Contact>,
		contactImport: InstanceType<typeof ContactImport>,
		unread: InstanceType<typeof Unread>,
		common: InstanceType<typeof Common>,
		cfc: InstanceType<typeof Cfc>,
		profile: InstanceType<typeof Profile>,
		communicator: InstanceType<typeof Communicator>
		icon: InstanceType<typeof Icon>
		color: InstanceType<typeof Color>
		teams: InstanceType<typeof Teams>
		timezone: InstanceType<typeof Timezone>
		country: InstanceType<typeof Country>
		companies: InstanceType<typeof Companies>
		callback: InstanceType<typeof Callback>
		calendar: InstanceType<typeof Calendar>
		recording: InstanceType<typeof Recording>
		dataDeletion: InstanceType<typeof DataDeletion>
		acl: InstanceType<typeof Acl>
		mailbox: InstanceType<typeof Mailbox>
		verify: InstanceType<typeof Verify>
		schedule: InstanceType<typeof Schedule>
		sound: InstanceType<typeof Sound>
		pbxDad: InstanceType<typeof PbxDad>
	};

	constructor(
		clientId: string,
		callback?: (event: string, b: boolean) => void,
		options?: Options
	) {
		this.accessToken = options?.accessToken ?? undefined
		this.headers = { headers: { Authorization: this.accessToken ?? '' } }
		this.oauthData = { access_token: this.accessToken ?? '' }
		this.user = undefined
		this.clientId = clientId
		this.useSAML = options?.useSAML ?? USE_SAML
		this.useRefreshToken = !!options?.useRefreshToken
		this.callback = callback ?? undefined
		this.storage = new Storage(options?.storageType ?? StorageTypes.sessionStorage)
		this.storedData = this.storage.getAll()
		this.saml = new SAML(
			clientId,
			this.storage,
			async (oauthData: any) => {
				this.oauthData = this.handleRefreshToken(oauthData);
				await this.setInitData();
			}
		);

		if(options?.apiUrl) {
			setApiUrl(options.apiUrl);
		}

		if(this.storedData?.authorization?.access_token) {
			this.accessToken = this.storedData.authorization.access_token
			this.oauthData = { access_token: this.accessToken }
			this.headers.headers.Authorization = `Bearer ${this.oauthData.access_token}`
		}

		if(this.storedData?.user) {
			this.user = this.storedData.user
		}

		this.calls = {
			login: new Login(this.headers),
			smsLogin: new SMSLogin(this.headers),
			user: new User(this.headers),
			company: new Company(this.headers),
			queue: new Queue(this.headers),
			captcha: new Captcha(this.headers),
			resetPassword: new ResetPassword(this.headers),
			contact: new Contact(this.headers),
			contactImport: new ContactImport(this.headers),
			unread: new Unread(this.headers),
			common: new Common(this.headers),
			cfc: new Cfc(this.headers),
			profile: new Profile(this.headers),
			communicator: new Communicator(this.headers),
			icon: new Icon(this.headers),
			color: new Color(this.headers),
			teams: new Teams(this.headers),
			timezone: new Timezone(this.headers),
			country: new Country(this.headers),
			companies: new Companies(this.headers),
			callback: new Callback(this.headers),
			calendar: new Calendar(this.headers),
			recording: new Recording(this.headers),
			dataDeletion: new DataDeletion(this.headers),
			acl: new Acl(this.headers),
			mailbox: new Mailbox(this.headers),
			verify: new Verify(this.headers),
			schedule: new Schedule(this.headers),
			sound: new Sound(this.headers),
			pbxDad: new PbxDad(this.headers),
		}

		if (this.useRefreshToken) {
			ApiStateListeners.addStateListener((event) => {
				if ((event === StateEvent.login || event === StateEvent.logout) && this.refreshTokenTimer) {
					clearTimeout(this.refreshTokenTimer);
				}

				if (event === StateEvent.login) {
					this.startRefreshTokenTimer();
				}
			});

			if (this.storedData?.authorization?.received && this.storedData?.authorization?.expires_in) {
				if ((Date.now() / 1000) >= this.getTokenExperationDate()) {
					this.refreshAccessToken()
					.catch((err) => console.error(err));
					return;
				} else {
					this.startRefreshTokenTimer();
				}
			}
		}

		this.checkSAML()
	}

	async refreshAccessToken() {
		const { refresh_token } = this.storage.get('authorization');

		if (refresh_token) {
			const result: any = await this.calls.login.refreshToken(this.clientId, refresh_token);
			result.data.received = Date.now() / 1000;
			this.oauthData = { ...result.data, refresh_token };
			this.accessToken = this.oauthData.access_token;
			this.updateHeaders(this.accessToken);
			this.storage.setFromObject({ authorization: this.oauthData });
			this.startRefreshTokenTimer();
		} else {
			console.error('Could not find a refresh_token');
			ApiStateListeners.fireStateListeners(StateEvent.invalidToken);
		}

		return;
	}

	private startRefreshTokenTimer() {
		const remainingTime = (this.getTokenExperationDate() - (Date.now() / 1000)) * 1000;

		if (remainingTime >= 0) {
			const timerId = setTimeout(() => {
				this.refreshAccessToken();
			}, remainingTime);
			this.refreshTokenTimer = timerId;
		} else {
			console.error('access token has already expired');
		}
	}

	/**
	 * @returns The token expiration date in seconds
	 */
	private getTokenExperationDate() {
		const { received, expires_in } = this.storage.get('authorization');

		return received + expires_in - 3600;
	}

	private handleRefreshToken(authorizationData: any) {
		const updatedAuthorizationData = { ...authorizationData };

		if (updatedAuthorizationData.refresh_token) {
			if (this.useRefreshToken) {
				updatedAuthorizationData.received = Date.now() / 1000;
			} else {
				delete updatedAuthorizationData.refresh_token;
			}
		}

		return updatedAuthorizationData;
	}

	private updateHeaders(accessToken?: string) {
		if (accessToken) {
			this.headers.headers.Authorization = `Bearer ${accessToken}`
			Object.values(this.calls).forEach((o: InstanceType<typeof Common>) => o.setHeaders(this.headers));
		} else {
			console.error('Trying to update headers with invalid access token');
			ApiStateListeners.fireStateListeners(StateEvent.invalidToken);
		}
	}

	async checkSAML() {
		if(this.useSAML && this.storedData?.SAML_COOKIE && this.storedData?.SAML_ALIAS) {
			await this.calls.login.samlAuthed(this.storedData.SAML_ALIAS, this.storedData.SAML_COOKIE)
			.then((r: any) => {
				this.oauthData = this.handleRefreshToken(r.data)
				this.setInitData()
				.then(() => {
					if(this.callback) {
						this.callback('samlLogin', true)
					}
				})
				.catch(() => console.error('SAML auth failed'))
				.finally(() => {
					this.storage.delete('SAML_COOKIE');
					this.storage.delete('SAML_ALIAS');
				})
			})
			.catch(() => console.error('SAML failed :S'))
		}
	}

	getUser() {
		return this.user;
	}

	isLoggedIn() {
		return !!this.accessToken
	}

	logout() {
		this.storage.clear()
		this.accessToken = undefined
		this.user = undefined
		this.oauthData = { access_token: '' }
		Object.values(this.calls).forEach((o: InstanceType<typeof Common>) => o.setHeaders({ headers: { Authorization: '' } }));
		ApiStateListeners.fireStateListeners(StateEvent.logout);
	}

	fireStateListeners(event: StateEvent) {
		ApiStateListeners.fireStateListeners(event);
	}

	addStateListener(listener: (e: StateEvent) => void): number {
		return ApiStateListeners.addStateListener(listener);
	}

	deleteStateListener(id: number): boolean {
		return ApiStateListeners.deleteStateListener(id);
	}

	async init(username: string, password: string, useSaml = true, tfa = "") {
		return new Promise((resolve, reject) => {
			(async () => {
				if(this.accessToken) {
					this.oauthData = { access_token: this.accessToken }
				} else {
					let exit = false

					if(useSaml && this.useSAML && !this.isLoggedIn()) {
						await this.calls.login.samlLogin(username, this.clientId)
						.then((r: any) => {
							exit = true
							this.storage.setFromObject({
								SAML_COOKIE: r.data.cookie,
								SAML_ALIAS: username,
							})

							window.location.href = r.data.url
							return
						})
						.catch(() => console.log('No SAML'))
					}

					if(exit) {
						return
					}

					if(!password) {
						reject()
						return
					}

					await this.calls.login.login(this.clientId, username, password, tfa)
					.then((r: any) => this.oauthData = this.handleRefreshToken(r.data))
					.catch(reject)
				}

				if (this.oauthData.access_token) {
					this.setInitData()
					.then(() => resolve(this.oauthData))
					.catch(reject)
				} else {
					console.error('Invalid access token');
				}
			})()
		})
	}

	async setInitData() {
		return new Promise((resolve, reject) => {
			try {
				this.updateHeaders(this.oauthData.access_token);
			} catch(error) {
				reject(error);
			}

			this.calls.user.read()
			.then((rr: any) => {
				this.user = rr.data

				// We can't save too much data for cookie and most of it is unneeded
				// if(this.storage.storageType === StorageTypes.cookie) {
				userThingsToDelete.forEach(t => {
					delete this.user[t]
				})
				// }

				this.storage.setFromObject({
					authorization: this.oauthData,
					user: this.user,
				})
				this.accessToken = this.oauthData.access_token
				resolve(true)
				ApiStateListeners.fireStateListeners(StateEvent.login);
			})
			.catch(reject)
		})
	}
}
